<template>
  <VContainer fluid class="pa-0 pb-12 mb-12">
    <SLoadingOverlay :loading="joinStore.loading || contentPending" />
    <CmsContent :html-content="pageData && pageData.content" />

    <!-- Agent test recruit could not be started or resumed (AB#64477). The retry re-runs the
         start, labelled as the agent's own Preview Join control; only an agent can start one,
         so a resumed test recruit gets the error alone and leaves through Exit. -->
    <VRow v-if="currentView === 'agent' && joinStore.error && !joinStore.loading" no-gutters justify="center">
      <VCol cols="12" sm="8" md="6" lg="4">
        <ErrorContainer :message="$ts(joinStore.error)" data-testid="join-agent-start-error" />
        <div class="text-center">
          <SBtn v-if="isAgent" ref="agentRetryButton" color="secondary" action data-testid="join-agent-start-retry" @click="retryAgentJoin">
            {{ $t('agent.btn.createRecruit') }}
          </SBtn>
        </div>
      </VCol>
    </VRow>

    <!-- Active Demonstrator Block -->
    <VRow v-if="isBlocked" no-gutters justify="center">
      <VCol cols="12" sm="8" md="6" lg="4">
        <VCard class="text-center pa-6">
          <VCardTitle class="text-uppercase font-weight-black">
            {{ $t('ida.label.demoApplication') }}
          </VCardTitle>
          <VCardText>
            <p v-if="joinStore.isActiveDemonstrator" data-testid="join-blocked-active-demo">
              {{ $ts('join.error.activeDemonstrator') }}
            </p>
            <template v-else-if="joinStore.isWithinCooldown">
              <p data-testid="join-blocked-cooldown">
                {{ $ts('join.error.cooldownNotEligible') }}
              </p>
              <p v-if="formattedCooldownEndDate" data-testid="join-cooldown-date" class="mt-2">
                {{ $ts('join.error.cooldownDate', { date: formattedCooldownEndDate }) }}
              </p>
              <p data-testid="join-cooldown-contact" class="mt-2">
                {{ $ts('join.error.cooldownContact') }}
              </p>
            </template>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Entry Modal -->
    <JoinEntryModal
      v-if="currentView === 'entry'"
      :show-dialog="showEntryModal"
      @update:show-dialog="showEntryModal = $event"
      @close="onEntryClose"
      @email-checked="onEmailChecked"
    />

    <!-- Welcome Back Modal (existing customer — screen 18) -->
    <WelcomeBackModal
      v-if="currentView === 'welcomeBack'"
      :show-dialog="showWelcomeBackModal"
      @update:show-dialog="showWelcomeBackModal = $event"
      @close="onClose"
      @authenticated="onAuthenticated"
      @forgot-password="onForgotPassword"
    />

    <!-- Account Registration Modal (new user — screen 17) -->
    <JoinRegistrationModal
      v-if="currentView === 'registration'"
      :show-dialog="showRegistrationModal"
      @update:show-dialog="showRegistrationModal = $event"
      @close="onClose"
      @authenticated="onRegistrationComplete"
    />
  </VContainer>
</template>

<script lang="ts" setup>
import CmsContent from '~/components/content/CmsContent.vue'
import ErrorContainer from '~/components/base/ErrorContainer.vue'
import JoinEntryModal from '~/components/join/JoinEntryModal.vue'
import WelcomeBackModal from '~/components/join/WelcomeBackModal.vue'
import JoinRegistrationModal from '~/components/join/JoinRegistrationModal.vue'
import { useJoinStore } from '~/stores/join'
import { useLocaleStore } from '~/stores/locale'
import { useNavigationStore } from '~/stores/navigation'
import { useRedirect, useSAuthState } from '~/composables/auth'
import { JoinStep } from '~/types/join'
import { UserRole } from '~/types/account'
import { JOIN_SELECT_OFFER } from '~/utils/joinRoutes'
import { useJoinTracking } from '~/composables/useJoinTracking'
import { useAgentJoinEntry } from '~/composables/useAgentJoinEntry'
import { findAttributionParam } from '~/utils/seo'

const route = useRoute()
const { redirect, redirectWithReload } = useRedirect()
const joinStore = useJoinStore()
const localeStore = useLocaleStore()
const navigationStore = useNavigationStore()
const { isAuthenticated, userRole, isAgent } = useSAuthState()

// The join landing has no `~/join` entry in the content index; source its CMS
// banner from the Community "Join Stampin' Up!" page instead (indexed under the
// footer/ prefix because Sitefinity links it from the footer's Community section).
const contentPath = 'footer/community/join-stampin-up'
const { pending: contentPending } = useLazyAsyncData('join-content', () => navigationStore.fetchHtmlContent(contentPath))
const pageData = computed(() => navigationStore.pageDataByKey(contentPath))

// 'agent': an agent's test recruit, starting or resuming -- no pop-up, and not the
// demonstrator 'blocked' card (which is keyed off isBlocked). AB#64477
type JoinView = 'entry' | 'welcomeBack' | 'blocked' | 'registration' | 'agent'

const showEntryModal = ref(false)
const showWelcomeBackModal = ref(false)
const showRegistrationModal = ref(false)
const currentView = ref<JoinView>('entry')
const isBlocked = ref(false)

const formattedCooldownEndDate = computed(() => {
  const raw = joinStore.cooldownEndDate
  if (!raw) return ''
  const date = new Date(raw)
  if (isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat(localeStore.culture, { dateStyle: 'long' }).format(date)
})

// Parse team leader ID from URL query params. My Links generates ?demoid=; it
// is read in any casing, like every other reader of that key.
const tracking = useJoinTracking()

const teamLeaderId = computed(() => {
  const referral = findAttributionParam(route.query)
  return (route.query.teamLeaderId as string) ?? (referral?.key === 'demoid' ? referral.value : null)
})

onMounted(() => {
  const agentStore = useAgentStore()
  useListen('untranslate', agentStore.untranslate)
  onBeforeUnmount(() => useUnlisten('untranslate', agentStore.untranslate))
  // Block active demonstrators from accessing join (client-only — auth state depends on cookies)
  if (isAuthenticated.value && userRole.value === UserRole.Demonstrator) {
    isBlocked.value = true
    currentView.value = 'blocked'
    joinStore.checkEmailResult = { exists: true, isActiveDemonstrator: true, isWithinCooldown: false }
    return
  }

  // Agent entries first: neither an agent nor an agent's test recruit reaches the guest
  // cases below. AB#64477
  if (enterAsAgent()) {
    currentView.value = 'agent'
    return
  }

  applySponsorFromLink()

  // If session has already progressed past auth, go to select offer page
  if (joinStore.session.email && joinStore.session.currentStep >= JoinStep.SelectOffer) {
    redirect(JOIN_SELECT_OFFER, true)
    return
  }

  // If mid-auth (welcome back), restore that view
  if (joinStore.session.currentStep === JoinStep.WelcomeBack) {
    currentView.value = 'welcomeBack'
    showWelcomeBackModal.value = true
    tracking.trackStepView('join_welcome_back')
    return
  }

  // Default: show entry modal (client-only to avoid VDialog classList SSR error)
  if (currentView.value === 'entry') {
    showEntryModal.value = true
    // Reported from the last branch in onMounted, so a blocked demonstrator or a session
    // already past entry has returned above and is never counted as viewing this step.
    tracking.trackStepView('join_entry')
  }
})

// Set step and go to select-offer. The session-resume branch in onMounted redirects on its
// own instead, because it must keep a currentStep already past SelectOffer. `replace` is for
// entries this page only passes through, so Back from select-offer does not land here and
// get pushed forward again.
const advanceToSelectOffer = ({ replace = false }: { replace?: boolean } = {}) => {
  joinStore.setStep(JoinStep.SelectOffer)
  redirect(JOIN_SELECT_OFFER, replace)
}

// URL param always wins: it overrides a stale session, so a fresh demo link preselects the
// right sponsor even on repeat visits. Applied on every entry path, the agent ones included.
const applySponsorFromLink = () => {
  if (teamLeaderId.value) {
    joinStore.setTeamLeaderId(teamLeaderId.value)
  }
}

// The agent entries (start, resume, leave-while-pending) live in useAgentJoinEntry. AB#64477
const { enterAsAgent, beginAgentJoin } = useAgentJoinEntry({ applySponsorFromLink, advanceToSelectOffer })

// The error row is swapped for the loader while the retry runs, so a failed retry renders a new
// button: focus it, or a keyboard user is left on the page body. The error announces itself.
const agentRetryButton = useTemplateRef('agentRetryButton')
const retryAgentJoin = async () => {
  await beginAgentJoin()
  if (joinStore.error) {
    await nextTick()
    agentRetryButton.value?.$el?.focus()
  }
}

const onEmailChecked = () => {
  const result = joinStore.checkEmailResult
  if (!result) return

  if (result.isActiveDemonstrator) {
    isBlocked.value = true
    currentView.value = 'blocked'
    showEntryModal.value = false
    return
  }

  if (result.isWithinCooldown) {
    isBlocked.value = true
    currentView.value = 'blocked'
    showEntryModal.value = false
    return
  }

  if (result.exists) {
    // Existing customer — show welcome back / password confirmation (screen 18)
    joinStore.setStep(JoinStep.WelcomeBack)
    currentView.value = 'welcomeBack'
    showEntryModal.value = false
    showWelcomeBackModal.value = true
    // The email check moves Entry -> WelcomeBack without navigating, so entry finishes here
    // and welcome-back begins. A URL watcher could never see either.
    tracking.trackStepCompleted('join_entry')
    tracking.trackStepView('join_welcome_back')
  } else {
    // New user — create account before proceeding (screen 17)
    showEntryModal.value = false
    currentView.value = 'registration'
    showRegistrationModal.value = true
    // Entry finishes on the email check for BOTH outcomes, so the step means the same thing
    // for a new and a returning applicant. Registration is its own section rather than being
    // folded into entry, which is what kept the two paths comparable in a funnel report.
    tracking.trackStepCompleted('join_entry')
    tracking.trackStepView('join_registration')
  }
}

const onAuthenticated = () => {
  // Existing customer authenticated — proceed to select offer
  showWelcomeBackModal.value = false
  tracking.trackStepCompleted('join_welcome_back')
  advanceToSelectOffer()
}

const onRegistrationComplete = async (password: string) => {
  // New account created + authenticated — establish recruit session then proceed
  showRegistrationModal.value = false
  const verified = await joinStore.verifyCustomer({
    username: joinStore.session.email,
    password,
  })
  // Only the funnel is gated on the outcome, deliberately NOT the navigation.
  //
  // By this point createAccount() and authenticate() have both succeeded — it is only the
  // recruit-session seeding that failed. Reopening the dialog would be a dead end, because
  // resubmitting calls createAccount() again for an email that now exists, which can only
  // fail; the applicant's sole exit would be ✕, which clears the session. So the pre-existing
  // carry-on behaviour stands, and what changes is that a failed verify is no longer recorded
  // as a completed step. Recovering properly needs a retry that re-runs verifyCustomer alone,
  // which is a UI change beyond this task.
  if (verified) { tracking.trackStepCompleted('join_registration') }
  advanceToSelectOffer()
}

const onForgotPassword = () => {
  redirect('/auth/password/reset')
}

// X on the entry ("Ready to Join") dialog should simply close it and keep the
// user on the join page — no redirect, no session clear (QA 12235381533).
const onEntryClose = () => {
  showEntryModal.value = false
}

const onClose = async () => {
  await joinStore.clearSession()
  // Full load, not router.push — see redirectWithReload (AB#63360).
  redirectWithReload('/')
}
</script>
